import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  NotebookPen, Plus, Search, Folder, Trash2, Edit2, X, Save,
  Youtube, ImagePlus, Tag, MoreVertical, AlertCircle,
  BookOpen, FolderPlus, Loader2, Image as ImageIcon,
  Grid3X3, List, Filter, Clipboard, History, ChevronLeft, ChevronRight,
  ZoomIn, PenLine, Clock, User, Eye, Share2, Check, Smile
} from 'lucide-react';
import Modal from './Modal';
import { NotepadCategory, NotepadNote, NoteHistory, NoteReaction } from '../types/notepad';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = '/api/notepad';
const MAX_IMAGES = 5;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

function getYouTubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]{11})/);
  return m ? m[1] : null;
}
function getYouTubeThumbnail(url: string): string | null {
  const id = getYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
}

const REACTION_EMOJIS = ['👍', '❤️', '😄', '🔥', '👏', '💡'];

const CATEGORY_COLORS = [
  '#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6',
  '#EC4899','#06B6D4','#84CC16','#F97316','#6366F1'
];

function formatDate(dateStr: string, locale = 'tr-TR'): string {
  return new Date(dateStr).toLocaleString(locale, { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

function parseArr(val: any): string[] {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') { try { return JSON.parse(val); } catch { return []; } }
  return [];
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────
interface LightboxProps { images: string[]; initialIndex: number; onClose: () => void; }
const Lightbox: React.FC<LightboxProps> = ({ images, initialIndex, onClose }) => {
  const [idx, setIdx] = useState(initialIndex);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIdx(i => Math.min(images.length - 1, i + 1));
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [images.length, onClose]);

  return (
    <div className="fixed inset-0 bg-black/90 z-[1000] flex items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white"><X size={28} /></button>
      {images.length > 1 && (
        <>
          <button onClick={e => { e.stopPropagation(); setIdx(i => Math.max(0, i - 1)); }}
            disabled={idx === 0}
            className="absolute left-4 text-white/80 hover:text-white disabled:opacity-30 p-2 bg-black/40 rounded-full">
            <ChevronLeft size={28} />
          </button>
          <button onClick={e => { e.stopPropagation(); setIdx(i => Math.min(images.length - 1, i + 1)); }}
            disabled={idx === images.length - 1}
            className="absolute right-4 text-white/80 hover:text-white disabled:opacity-30 p-2 bg-black/40 rounded-full">
            <ChevronRight size={28} />
          </button>
        </>
      )}
      <img
        src={images[idx]}
        alt=""
        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={e => e.stopPropagation()}
      />
      {images.length > 1 && (
        <div className="absolute bottom-4 flex gap-2">
          {images.map((_, i) => (
            <button key={i} onClick={e => { e.stopPropagation(); setIdx(i); }}
              className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white dark:bg-gray-800 scale-125' : 'bg-white/40'}`} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Geçmiş Sekmesi ───────────────────────────────────────────────────────────
interface NoteHistoryPanelProps { noteId: number; }
const NoteHistoryPanel: React.FC<NoteHistoryPanelProps> = ({ noteId }) => {
  const { t, language } = useLanguage();
  const [history, setHistory] = useState<NoteHistory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/notes/${noteId}/history`, { headers: authHeaders() })
      .then(r => setHistory(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [noteId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-primary" /></div>;
  if (!history.length) return (
    <div className="flex flex-col items-center py-10 text-gray-400">
      <History size={36} className="mb-2 opacity-30" />
      <p className="text-sm">{t('notepad.historyEmpty')}</p>
    </div>
  );

  return (
    <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
      {history.map(h => (
        <div key={h.id} className="flex gap-3">
          <div className="flex-shrink-0 mt-0.5">
            {h.user_avatar_fresh || h.user_avatar
              ? <img src={h.user_avatar_fresh || h.user_avatar} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-gray-700" />
              : <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center"><User size={14} className="text-primary" /></div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{h.user_name || t('notepad.unknown')}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${h.action === 'created' ? 'bg-green-100 text-green-700' : 'bg-primary/20 text-primary-dark'}`}>
                {h.action === 'created' ? t('notepad.actionCreated') : t('notepad.actionUpdated')}
              </span>
              <span className="text-xs text-gray-400 flex items-center gap-0.5 ml-auto"><Clock size={11} /> {formatDate(h.created_at, language === 'en' ? 'en-US' : 'tr-TR')}</span>
            </div>
            {parseArr(h.changes).length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {parseArr(h.changes).map((c: any, i: number) => (
                  <li key={i} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-1">
                    <PenLine size={11} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-medium">{c.label}:</span>{' '}
                      {c.field === 'content'
                        ? <span className="italic text-gray-500 dark:text-gray-400">{t('notepad.contentUpdated')}</span>
                        : <><span className="line-through text-gray-400">{c.from}</span> → <span className="text-gray-700 dark:text-gray-300">{c.to}</span></>
                      }
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Not Önizleme Modal ───────────────────────────────────────────────────────
interface NotePreviewModalProps { isOpen: boolean; onClose: () => void; note: NotepadNote | null; onEdit: () => void; currentUserId?: number | null; }
const NotePreviewModal: React.FC<NotePreviewModalProps> = ({ isOpen, onClose, note, onEdit, currentUserId }) => {
  const { t, language } = useLanguage();
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [reactions, setReactions] = useState<NoteReaction[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadReactions = async (noteId: number) => {
    try {
      const res = await axios.get(`${API}/notes/${noteId}/reactions`, { headers: authHeaders() });
      setReactions(res.data);
    } catch {}
  };

  useEffect(() => {
    if (isOpen && note) { loadReactions(note.id); setShowEmojiPicker(false); }
    else { setReactions([]); }
  }, [isOpen, note?.id]);

  const toggleReaction = async (emoji: string) => {
    if (!note) return;
    try {
      await axios.post(`${API}/notes/${note.id}/reactions`, { emoji }, { headers: authHeaders() });
      loadReactions(note.id);
    } catch {}
    setShowEmojiPicker(false);
  };

  const handleShare = () => {
    if (!note) return;
    const url = `${window.location.origin}/notepad?note=${note.id}`;
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); }).catch(() => {});
  };

  if (!note) return null;
  const images = parseArr(note.images);
  const ytLinks = parseArr(note.youtube_links);

  return (
    <>
      {lightboxIdx !== null && <Lightbox images={images} initialIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />}
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 md:p-6">
            {/* Başlık */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1 min-w-0 pr-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 break-words">{note.title}</h2>
                {note.category_name && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mt-2"
                    style={{ backgroundColor: (note.category_color || '#3B82F6') + '20', color: note.category_color || '#3B82F6' }}>
                    <Folder size={10} /> {note.category_name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={handleShare} title={t('notepad.copyLink')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${copied ? 'border-green-500 text-green-600 bg-green-50' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'}`}>
                  {copied ? <><Check size={14} /> {t('notepad.copied')}</> : <><Share2 size={14} /> {t('notepad.share')}</>}
                </button>
                <button onClick={() => { onClose(); onEdit(); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors">
                  <Edit2 size={14} /> {t('notepad.edit')}
                </button>
                <button onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"><X className="w-5 h-5" /></button>
              </div>
            </div>

            {/* İçerik */}
            {note.content && (
              <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg leading-relaxed">
                {note.content}
              </div>
            )}

            {/* Görseller */}
            {images.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
                  <ImageIcon size={12} /> {t('notepad.images')} ({images.length})
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {images.map((img, i) => (
                    <div key={i} className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 aspect-video bg-gray-100 dark:bg-gray-700 cursor-pointer"
                      onClick={() => setLightboxIdx(i)}>
                      <img src={img} alt={`görsel-${i}`} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                        <ZoomIn size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* YouTube */}
            {ytLinks.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
                  <Youtube size={12} /> YouTube ({ytLinks.length})
                </p>
                <div className="space-y-2">
                  {ytLinks.map((url, i) => {
                    const thumb = getYouTubeThumbnail(url);
                    return (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        {thumb && <img src={thumb} alt="" className="w-20 h-12 object-cover rounded flex-shrink-0" />}
                        <span className="text-xs text-primary truncate">{url}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ─── Reaksiyonlar ─── */}
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-3">
              <div className="flex items-center flex-wrap gap-2">
                {reactions.map(r => {
                  const myReaction = currentUserId ? r.user_ids.includes(currentUserId) : false;
                  return (
                    <button key={r.emoji} onClick={() => toggleReaction(r.emoji)} title={r.user_names.join(', ')}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-sm border transition-all ${myReaction ? 'bg-primary/10 border-primary/50 text-primary-dark font-medium' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                      <span>{r.emoji}</span>
                      <span className="text-xs">{r.count}</span>
                    </button>
                  );
                })}
                {/* Emoji Ekle butonu */}
                <div className="relative">
                  <button onClick={() => setShowEmojiPicker(p => !p)} title={t('notepad.addReaction')}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full text-sm border border-dashed border-gray-300 dark:border-gray-600 text-gray-400 hover:border-gray-400 hover:text-gray-600 dark:text-gray-400 transition-all">
                    <Smile size={14} /> <span className="text-xs">{t('notepad.addEmoji')}</span>
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-9 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2 flex gap-1.5 z-10">
                      {REACTION_EMOJIS.map(e => (
                        <button key={e} onClick={() => toggleReaction(e)}
                          className="text-xl hover:scale-125 transition-transform p-0.5 rounded" title={e}>
                          {e}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Meta */}
            <div className="mt-3 flex items-center justify-between flex-wrap gap-2 text-xs text-gray-400">
              {note.created_by_name && (
                <span className="flex items-center gap-1.5">
                  {note.created_by_avatar
                    ? <img src={note.created_by_avatar} alt="" className="w-4 h-4 rounded-full object-cover" />
                    : <User size={12} />}
                  {note.created_by_name}
                </span>
              )}
              <span className="flex items-center gap-1"><Clock size={11} /> {formatDate(note.updated_at, language === 'en' ? 'en-US' : 'tr-TR')}</span>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Kategori Modal ───────────────────────────────────────────────────────────
interface CategoryModalProps { isOpen: boolean; onClose: () => void; onSave: (cat: Partial<NotepadCategory>) => Promise<void>; initial?: NotepadCategory | null; }
const CategoryModal: React.FC<CategoryModalProps> = ({ isOpen, onClose, onSave, initial }) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initial) { setName(initial.name); setColor(initial.color); }
    else { setName(''); setColor('#3B82F6'); }
    setError('');
  }, [initial, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) { setError(t('notepad.errCatNameRequired')); return; }
    setSaving(true);
    try { await onSave({ name, color, icon: 'Folder' }); onClose(); }
    catch (e: any) { setError(e.response?.data?.error || t('notepad.errGeneral')); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          <div className="flex justify-between items-center mb-4 md:mb-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">{initial ? t('notepad.catEditTitle') : t('notepad.catNewTitle')}</h2>
            <button onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"><X className="w-5 h-5" /></button>
          </div>
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">{error}</div>}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('notepad.catNameLabel')}</label>
              <input className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={name} onChange={e => setName(e.target.value)} placeholder={t('notepad.catNamePlaceholder')} onKeyDown={e => e.key === 'Enter' && handleSave()} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('notepad.catColorLabel')}</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_COLORS.map(c => (
                  <button key={c} onClick={() => setColor(c)}
                    className={`w-7 h-7 rounded-full border-2 transition-all ${color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
            <button onClick={onClose} className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">{t('notepad.cancel')}</button>
            <button onClick={handleSave} disabled={saving} className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 flex items-center justify-center gap-1">
              {saving && <Loader2 size={14} className="animate-spin" />} {t('notepad.save')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ─── Not Modal ────────────────────────────────────────────────────────────────
interface NoteModalProps {
  isOpen: boolean; onClose: () => void;
  onSave: (note: Partial<NotepadNote>) => Promise<void>;
  onCreateCategory: (data: Partial<NotepadCategory>) => Promise<NotepadCategory>;
  initial?: NotepadNote | null;
  categories: NotepadCategory[];
  defaultCategoryId?: number | null;
}

const NoteModal: React.FC<NoteModalProps> = ({ isOpen, onClose, onSave, onCreateCategory, initial, categories, defaultCategoryId }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'form' | 'history'>('form');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [youtubeLinks, setYoutubeLinks] = useState<string[]>([]);
  const [ytInput, setYtInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const initializedForId = useRef<number | 'new' | null>(null);

  // Hızlı kategori
  const [quickCatOpen, setQuickCatOpen] = useState(false);
  const [quickCatName, setQuickCatName] = useState('');
  const [quickCatColor, setQuickCatColor] = useState('#3B82F6');
  const [quickCatSaving, setQuickCatSaving] = useState(false);
  const quickCatInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) { initializedForId.current = null; return; }
    const noteId = initial?.id ?? 'new';
    if (initializedForId.current === noteId) return;
    initializedForId.current = noteId;
    setActiveTab('form');
    if (initial) {
      setTitle(initial.title); setContent(initial.content || '');
      setCategoryId(initial.category_id);
      setImages(parseArr(initial.images)); setYoutubeLinks(parseArr(initial.youtube_links));
    } else {
      setTitle(''); setContent('');
      setCategoryId(defaultCategoryId ?? null);
      setImages([]); setYoutubeLinks([]);
    }
    setYtInput(''); setError('');
    setQuickCatOpen(false); setQuickCatName(''); setQuickCatColor('#3B82F6');
  }, [initial, isOpen, defaultCategoryId]);

  // ─── Görsel Yükleme ───────────────────────────────────────────────────────
  const uploadFiles = async (files: File[]) => {
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) { setError(t('notepad.errMaxImages').replace('{count}', String(MAX_IMAGES))); return; }
    const toUpload = files.slice(0, remaining);
    if (toUpload.length < files.length) setError(t('notepad.errMaxImagesPartial').replace('{uploaded}', String(toUpload.length)).replace('{count}', String(MAX_IMAGES)));
    setUploading(true);
    try {
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append('image', file);
        const res = await axios.post(`${API}/upload-image`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
        setImages(prev => [...prev, res.data.path]);
      }
    } catch { setError(t('notepad.errImageUpload')); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => uploadFiles(Array.from(e.target.files || []));

  const handlePaste = (e: React.ClipboardEvent) => {
    const imageFiles = Array.from(e.clipboardData.items)
      .filter(item => item.type.startsWith('image/'))
      .map(item => {
        const f = item.getAsFile();
        if (!f) return null;
        // Uzantı yoksa MIME türüne göre ekle
        if (f.name && f.name.includes('.')) return f;
        const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
        return new File([f], `clipboard-${Date.now()}.${ext}`, { type: f.type });
      })
      .filter((f): f is File => f !== null);
    if (imageFiles.length) { e.preventDefault(); uploadFiles(imageFiles); }
  };

  const handleClipboardPaste = async () => {
    try {
      const items = await navigator.clipboard.read();
      const imageFiles: File[] = [];
      for (const item of items) {
        const imageType = item.types.find(t => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          imageFiles.push(new File([blob], `paste-${Date.now()}.png`, { type: imageType }));
        }
      }
      if (imageFiles.length) uploadFiles(imageFiles);
      else setError(t('notepad.errNoImageClipboard'));
    } catch { setError(t('notepad.errClipboardDenied')); }
  };

  const handleQuickCatOpen = () => {
    setQuickCatOpen(true); setQuickCatName(''); setQuickCatColor('#3B82F6');
    setTimeout(() => quickCatInputRef.current?.focus(), 50);
  };

  const handleQuickCatSave = async () => {
    if (!quickCatName.trim()) return;
    setQuickCatSaving(true);
    try {
      const newCat = await onCreateCategory({ name: quickCatName.trim(), color: quickCatColor, icon: 'Folder' });
      setCategoryId(newCat.id); setQuickCatOpen(false); setQuickCatName('');
    } catch { setError(t('notepad.errCatCreate')); }
    finally { setQuickCatSaving(false); }
  };

  const handleSave = async () => {
    if (!title.trim()) { setError(t('notepad.errTitleRequired')); return; }
    setSaving(true);
    try {
      await onSave({ category_id: categoryId, title, content, images, youtube_links: youtubeLinks });
      onClose();
    } catch (e: any) { setError(e.response?.data?.error || t('notepad.errGeneral')); }
    finally { setSaving(false); }
  };

  const addYouTubeLink = () => {
    const url = ytInput.trim();
    if (!url) return;
    if (!getYouTubeId(url)) { setError(t('notepad.errInvalidYoutube')); return; }
    if (youtubeLinks.includes(url)) { setError(t('notepad.errYoutubeDuplicate')); return; }
    setYoutubeLinks(prev => [...prev, url]); setYtInput(''); setError('');
  };

  return (
    <>
      {lightboxIdx !== null && <Lightbox images={images} initialIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />}
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 md:p-6">
            {/* Başlık */}
            <div className="flex justify-between items-center mb-4 md:mb-6">
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <NotebookPen size={20} className="text-primary" />
                {initial ? t('notepad.noteEditTitle') : t('notepad.noteNewTitle')}
              </h2>
              <button onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"><X className="w-5 h-5" /></button>
            </div>

            {/* Sekmeler (sadece düzenlemede) */}
            {initial && (
              <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
                <button onClick={() => setActiveTab('form')}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === 'form' ? 'border-primary text-primary' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'}`}>
                  <span className="flex items-center gap-1.5"><NotebookPen size={14} /> {t('notepad.tabNote')}</span>
                </button>
                <button onClick={() => setActiveTab('history')}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === 'history' ? 'border-primary text-primary' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'}`}>
                  <span className="flex items-center gap-1.5"><History size={14} /> {t('notepad.tabHistory')}</span>
                </button>
              </div>
            )}

            {/* Hata */}
            {error && activeTab === 'form' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600 flex items-center gap-2">
                <AlertCircle size={16} /> {error}
                <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
              </div>
            )}

            {/* ─── Form Sekmesi ─── */}
            {activeTab === 'form' && (
              <div className="space-y-4" onPaste={handlePaste}>
                {/* Başlık */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('notepad.titleLabel')}</label>
                  <input className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={title} onChange={e => setTitle(e.target.value)} placeholder={t('notepad.titlePlaceholder')} />
                </div>

                {/* Kategori */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('notepad.categoryLabel')}</label>
                    {!quickCatOpen && (
                      <button type="button" onClick={handleQuickCatOpen} className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-medium">
                        <Plus size={13} /> {t('notepad.quickCreate')}
                      </button>
                    )}
                  </div>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={categoryId ?? ''} onChange={e => setCategoryId(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">{t('notepad.uncategorizedOption')}</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {quickCatOpen && (
                    <div className="mt-2 p-3 border border-primary/30 rounded-lg bg-primary/10 space-y-2">
                      <p className="text-xs font-medium text-primary-dark">{t('notepad.newCategoryQuick')}</p>
                      <input ref={quickCatInputRef}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800"
                        value={quickCatName} onChange={e => setQuickCatName(e.target.value)} placeholder={t('notepad.catNamePlaceholder')}
                        onKeyDown={e => { if (e.key === 'Enter') handleQuickCatSave(); if (e.key === 'Escape') setQuickCatOpen(false); }} />
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1.5 flex-wrap flex-1">
                          {CATEGORY_COLORS.map(c => (
                            <button key={c} type="button" onClick={() => setQuickCatColor(c)}
                              className={`w-5 h-5 rounded-full border-2 transition-all ${quickCatColor === c ? 'border-gray-700 scale-110' : 'border-transparent'}`}
                              style={{ backgroundColor: c }} />
                          ))}
                        </div>
                        <button type="button" onClick={() => setQuickCatOpen(false)} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 px-2 py-1 rounded">{t('notepad.cancel')}</button>
                        <button type="button" onClick={handleQuickCatSave} disabled={quickCatSaving || !quickCatName.trim()}
                          className="flex items-center gap-1 text-xs bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary-dark disabled:opacity-50">
                          {quickCatSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} {t('notepad.addEmoji')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* İçerik */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('notepad.contentLabel')}</label>
                  <textarea className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                    rows={6} value={content} onChange={e => setContent(e.target.value)} placeholder={t('notepad.contentPlaceholder')} />
                </div>

                {/* Görseller */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('notepad.imagesLabel')}
                      <span className={`ml-2 text-xs font-normal ${images.length >= MAX_IMAGES ? 'text-red-500' : 'text-gray-400'}`}>
                        ({images.length}/{MAX_IMAGES})
                      </span>
                    </label>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={handleClipboardPaste} disabled={uploading || images.length >= MAX_IMAGES}
                        title={t('notepad.pasteTitle')}
                        className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-primary font-medium disabled:opacity-40">
                        <Clipboard size={13} /> {t('notepad.paste')}
                      </button>
                      <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || images.length >= MAX_IMAGES}
                        className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-medium disabled:opacity-40">
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                        {uploading ? t('notepad.uploading') : t('notepad.selectFile')}
                      </button>
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    {t('notepad.ctrlVHint').split('Ctrl+V')[0]}<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs">Ctrl+V</kbd>{t('notepad.ctrlVHint').split('Ctrl+V')[1]}
                    {images.length >= MAX_IMAGES && <span className="ml-1 text-red-500 font-medium">{t('notepad.maxImagesReached')}</span>}
                  </p>
                  {images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {images.map((img, i) => (
                        <div key={i} className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 aspect-video bg-gray-100 dark:bg-gray-700 cursor-pointer"
                          onClick={() => setLightboxIdx(i)}>
                          <img src={img} alt={`görsel-${i}`} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                            <ZoomIn size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <button onClick={e => { e.stopPropagation(); setImages(prev => prev.filter((_, j) => j !== i)); }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* YouTube */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('notepad.youtubeLabel')}</label>
                  <div className="flex gap-2">
                    <input className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      value={ytInput} onChange={e => setYtInput(e.target.value)} placeholder={t('notepad.youtubePlaceholder')}
                      onKeyDown={e => e.key === 'Enter' && addYouTubeLink()} />
                    <button onClick={addYouTubeLink} className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-1 text-sm">
                      <Youtube size={16} /> {t('notepad.addEmoji')}
                    </button>
                  </div>
                  {youtubeLinks.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {youtubeLinks.map((url, i) => {
                        const thumb = getYouTubeThumbnail(url);
                        return (
                          <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                            {thumb && <img src={thumb} alt="" className="w-16 h-9 object-cover rounded" />}
                            <span className="flex-1 text-xs text-gray-600 dark:text-gray-400 truncate">{url}</span>
                            <button onClick={() => setYoutubeLinks(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X size={16} /></button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ─── Geçmiş Sekmesi ─── */}
            {activeTab === 'history' && initial && (
              <NoteHistoryPanel noteId={initial.id} />
            )}

            {/* Footer */}
            {activeTab === 'form' && (
              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                <button onClick={onClose} className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">{t('notepad.cancel')}</button>
                <button onClick={handleSave} disabled={saving || uploading}
                  title={uploading ? t('notepad.uploading') : undefined}
                  className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 flex items-center justify-center gap-1">
                  {(saving || uploading) ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {uploading ? t('notepad.uploading') : t('notepad.save')}
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Not Kartı ────────────────────────────────────────────────────────────────
interface NoteCardProps { note: NotepadNote; onEdit: () => void; onDelete: () => void; onPreview: () => void; view: 'grid' | 'list'; onImageClick: (images: string[], idx: number) => void; }
const NoteCard: React.FC<NoteCardProps> = ({ note, onEdit, onDelete, onPreview, view, onImageClick }) => {
  const { t, language } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/notepad?note=${note.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setShareCopied(true); setMenuOpen(false);
      setTimeout(() => setShareCopied(false), 2500);
    }).catch(() => {});
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const images = parseArr(note.images);
  const ytLinks = parseArr(note.youtube_links);
  const hasMedia = images.length > 0 || ytLinks.length > 0;

  if (view === 'list') {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-start gap-4 hover:shadow-md transition-shadow cursor-pointer group/card"
        onClick={onPreview}>
        <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: note.category_color || '#E5E7EB' }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 truncate group-hover/card:text-primary transition-colors">{note.title}</h3>
              {note.category_name && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mt-1"
                  style={{ backgroundColor: (note.category_color || '#3B82F6') + '20', color: note.category_color || '#3B82F6' }}>
                  <Folder size={10} /> {note.category_name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
              <button onClick={e => { e.stopPropagation(); onEdit(); }}
                className="text-gray-400 hover:text-primary p-1 rounded opacity-0 group-hover/card:opacity-100 transition-opacity" title={t('notepad.edit')}>
                <Edit2 size={14} />
              </button>
              <div className="relative" ref={menuRef}>
                <button onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen); }} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 p-1 rounded"><MoreVertical size={16} /></button>
                {menuOpen && (
                  <div className="absolute right-0 top-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10 w-36">
                    <button onClick={() => { setMenuOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 flex items-center gap-2"><Edit2 size={14} /> {t('notepad.edit')}</button>
                    <button onClick={handleShare} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 flex items-center gap-2">
                      {shareCopied ? <><Check size={14} className="text-green-500" /> {t('notepad.copied')}</> : <><Share2 size={14} /> {t('notepad.share')}</>}
                    </button>
                    <button onClick={() => { setMenuOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><Trash2 size={14} /> {t('notepad.delete')}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {note.content && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{note.content}</p>}
          {images.length > 0 && (
            <div className="flex gap-1.5 mt-2 flex-wrap" onClick={e => e.stopPropagation()}>
              {images.slice(0, 3).map((img, i) => (
                <img key={i} src={img} alt="" onClick={() => onImageClick(images, i)}
                  className="w-14 h-10 object-cover rounded border border-gray-200 dark:border-gray-700 cursor-pointer hover:opacity-90 transition-opacity" />
              ))}
              {images.length > 3 && <div onClick={() => onImageClick(images, 3)} className="w-14 h-10 rounded border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600">+{images.length - 3}</div>}
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
            {hasMedia && <span className="flex items-center gap-1">{images.length > 0 && <><ImageIcon size={12} /> {images.length}</>}{ytLinks.length > 0 && <><Youtube size={12} className="ml-1" /> {ytLinks.length}</>}</span>}
            <span>{formatDate(note.updated_at, language === 'en' ? 'en-US' : 'tr-TR')}</span>
            <span className="ml-auto flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity text-primary"><Eye size={12} /> {t('notepad.preview')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:shadow-md transition-shadow flex flex-col cursor-pointer group/card"
      onClick={onPreview}>
      {images.length > 0 && (
        <div className="relative h-36 bg-gray-100 dark:bg-gray-700 overflow-hidden" onClick={e => { e.stopPropagation(); onImageClick(images, 0); }}>
          <img src={images[0]} alt="" className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-200" />
          <div className="absolute inset-0 bg-black/0 group-hover/card:bg-black/20 transition-all flex items-center justify-center">
            <ZoomIn size={24} className="text-white opacity-0 group-hover/card:opacity-100 transition-opacity" />
          </div>
          {images.length > 1 && <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">+{images.length - 1}</span>}
        </div>
      )}
      {images.length === 0 && ytLinks.length > 0 && (() => {
        const thumb = getYouTubeThumbnail(ytLinks[0]);
        return thumb ? (
          <div className="relative h-36 bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <img src={thumb} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20"><Youtube size={36} className="text-white" /></div>
          </div>
        ) : null;
      })()}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200 line-clamp-2 flex-1 group-hover/card:text-primary transition-colors">{note.title}</h3>
          <div className="relative flex-shrink-0" ref={menuRef} onClick={e => e.stopPropagation()}>
            <button onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen); }} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 p-0.5 rounded"><MoreVertical size={16} /></button>
            {menuOpen && (
              <div className="absolute right-0 top-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10 w-36">
                <button onClick={() => { setMenuOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 flex items-center gap-2"><Edit2 size={14} /> {t('notepad.edit')}</button>
                <button onClick={handleShare} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 flex items-center gap-2">
                  {shareCopied ? <><Check size={14} className="text-green-500" /> {t('notepad.copied')}</> : <><Share2 size={14} /> {t('notepad.share')}</>}
                </button>
                <button onClick={() => { setMenuOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><Trash2 size={14} /> {t('notepad.delete')}</button>
              </div>
            )}
          </div>
        </div>
        {note.category_name && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mb-2 self-start"
            style={{ backgroundColor: (note.category_color || '#3B82F6') + '20', color: note.category_color || '#3B82F6' }}>
            <Folder size={10} /> {note.category_name}
          </span>
        )}
        {note.content && <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-3 flex-1">{note.content}</p>}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            {images.length > 0 && <span className="flex items-center gap-0.5"><ImageIcon size={12} /> {images.length}</span>}
            {ytLinks.length > 0 && <span className="flex items-center gap-0.5"><Youtube size={12} /> {ytLinks.length}</span>}
          </div>
          <span className="text-xs text-gray-400 flex items-center gap-1 group-hover/card:text-primary transition-colors">
            <Eye size={11} className="opacity-0 group-hover/card:opacity-100 transition-opacity" />
            {formatDate(note.updated_at, language === 'en' ? 'en-US' : 'tr-TR')}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────
const NotepadPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [categories, setCategories] = useState<NotepadCategory[]>([]);
  const [notes, setNotes] = useState<NotepadNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const urlNoteHandled = useRef(false);

  const [catModalOpen, setCatModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<NotepadCategory | null>(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<NotepadNote | null>(null);
  const [previewNote, setPreviewNote] = useState<NotepadNote | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'category' | 'note'; id: number; name: string } | null>(null);
  const [error, setError] = useState('');

  // Global lightbox (not kartlarından açılır)
  const [lightbox, setLightbox] = useState<{ images: string[]; idx: number } | null>(null);

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(searchTerm), 300); return () => clearTimeout(t); }, [searchTerm]);

  const loadCategories = useCallback(async () => {
    try { const res = await axios.get(`${API}/categories`, { headers: authHeaders() }); setCategories(res.data); }
    catch { setError(t('notepad.errLoadCategories')); }
  }, []);

  const loadNotes = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (selectedCategory !== 'all') params.category_id = selectedCategory;
      const res = await axios.get(`${API}/notes`, { headers: authHeaders(), params });
      setNotes(res.data);
    } catch { setError(t('notepad.errLoadNotes')); }
  }, [debouncedSearch, selectedCategory]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadCategories(), loadNotes()]).finally(() => setLoading(false));
  }, [loadCategories, loadNotes]);

  // URL'de ?note=ID varsa o notu önizle (paylaşım linki)
  useEffect(() => {
    if (loading || urlNoteHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const noteId = params.get('note');
    if (!noteId) return;
    urlNoteHandled.current = true;
    window.history.replaceState(null, '', '/notepad');

    const found = notes.find(n => String(n.id) === noteId);
    if (found) { setPreviewNote(found); return; }

    // Notlar listede yoksa (filtre vs.) API'dan tek not çek
    axios.get(`${API}/notes/${noteId}`, { headers: authHeaders() })
      .then(res => setPreviewNote(res.data))
      .catch(() => {});
  }, [loading, notes]);

  const createCategory = async (data: Partial<NotepadCategory>): Promise<NotepadCategory> => {
    const res = await axios.post(`${API}/categories`, data, { headers: authHeaders() });
    await loadCategories();
    return res.data;
  };

  const saveCategory = async (data: Partial<NotepadCategory>) => {
    if (editingCategory) {
      await axios.put(`${API}/categories/${editingCategory.id}`, data, { headers: authHeaders() });
    } else {
      await createCategory(data);
      return;
    }
    await loadCategories();
    setEditingCategory(null);
  };

  const deleteCategory = async (id: number) => {
    await axios.delete(`${API}/categories/${id}`, { headers: authHeaders() });
    if (selectedCategory === String(id)) setSelectedCategory('all');
    await Promise.all([loadCategories(), loadNotes()]);
  };

  const saveNote = async (data: Partial<NotepadNote>) => {
    if (editingNote) {
      await axios.put(`${API}/notes/${editingNote.id}`, data, { headers: authHeaders() });
    } else {
      await axios.post(`${API}/notes`, data, { headers: authHeaders() });
    }
    await loadNotes();
  };

  const deleteNote = async (id: number) => {
    await axios.delete(`${API}/notes/${id}`, { headers: authHeaders() });
    await loadNotes();
  };

  const defaultCategoryId = selectedCategory !== 'all' && selectedCategory !== 'uncategorized' ? Number(selectedCategory) : null;

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-700 relative">
      {lightbox && <Lightbox images={lightbox.images} initialIndex={lightbox.idx} onClose={() => setLightbox(null)} />}

      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-20 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed md:relative z-30 md:z-auto top-0 left-0 h-full w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-base font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2"><NotebookPen size={18} className="text-primary" /> {t('notepad.sidebarTitle')}</h1>
            <button className="md:hidden text-gray-400" onClick={() => setSidebarOpen(false)}><X size={18} /></button>
          </div>
          <button onClick={() => { setEditingNote(null); setNoteModalOpen(true); setSidebarOpen(false); }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors">
            <Plus size={16} /> {t('notepad.newNote')}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('notepad.categories')}</span>
            <button onClick={() => { setEditingCategory(null); setCatModalOpen(true); }} className="text-gray-400 hover:text-primary p-0.5 rounded" title={t('notepad.newCategoryTitle')}><FolderPlus size={16} /></button>
          </div>
          <ul className="space-y-0.5">
            <li>
              <button onClick={() => { setSelectedCategory('all'); setSidebarOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${selectedCategory === 'all' ? 'bg-primary/10 text-primary-dark font-medium' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700'}`}>
                <span className="flex items-center gap-2"><BookOpen size={15} /> {t('notepad.allNotes')}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${selectedCategory === 'all' ? 'bg-primary/20 text-primary-dark' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>{notes.length}</span>
              </button>
            </li>
            {categories.map(cat => (
              <li key={cat.id}>
                <div className={`group flex items-center rounded-lg transition-colors ${selectedCategory === String(cat.id) ? 'bg-primary/10' : 'hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700'}`}>
                  <button onClick={() => { setSelectedCategory(String(cat.id)); setSidebarOpen(false); }} className="flex-1 flex items-center gap-2 px-3 py-2 text-sm text-left">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                    <span className={`truncate ${selectedCategory === String(cat.id) ? 'text-primary-dark font-medium' : 'text-gray-600 dark:text-gray-400'}`}>{cat.name}</span>
                    <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${selectedCategory === String(cat.id) ? 'bg-primary/20 text-primary-dark' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>{cat.note_count ?? 0}</span>
                  </button>
                  <div className="flex items-center gap-0.5 pr-2 opacity-0 group-hover:opacity-100">
                    <button onClick={e => { e.stopPropagation(); setEditingCategory(cat); setCatModalOpen(true); }} className="text-gray-400 hover:text-primary p-1 rounded"><Edit2 size={13} /></button>
                    <button onClick={e => { e.stopPropagation(); setDeleteConfirm({ type: 'category', id: cat.id, name: cat.name }); }} className="text-gray-400 hover:text-red-600 p-1 rounded"><Trash2 size={13} /></button>
                  </div>
                </div>
              </li>
            ))}
            <li>
              <button onClick={() => { setSelectedCategory('uncategorized'); setSidebarOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${selectedCategory === 'uncategorized' ? 'bg-primary/10 text-primary-dark font-medium' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700'}`}>
                <span className="flex items-center gap-2"><Tag size={15} /> {t('notepad.uncategorized')}</span>
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      {/* Ana İçerik */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Üst Bar */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <button className="md:hidden text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 p-1" onClick={() => setSidebarOpen(true)}><Filter size={20} /></button>
          <div className="flex-1 relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 dark:bg-gray-700"
              placeholder={t('notepad.searchPlaceholder')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-400"><X size={14} /></button>}
          </div>
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            <button onClick={() => setView('grid')} className={`p-2 ${view === 'grid' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'}`}><Grid3X3 size={16} /></button>
            <button onClick={() => setView('list')} className={`p-2 ${view === 'list' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'}`}><List size={16} /></button>
          </div>
          <button onClick={() => { setEditingNote(null); setNoteModalOpen(true); }}
            className="hidden md:flex items-center gap-1 px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark">
            <Plus size={16} /> {t('notepad.newNote')}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
              <AlertCircle size={16} /> {error}
              <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-48"><Loader2 size={32} className="animate-spin text-primary" /></div>
          ) : notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <NotebookPen size={48} className="mb-3 opacity-30" />
              <p className="text-sm font-medium">{debouncedSearch ? t('notepad.noResults') : t('notepad.empty')}</p>
              {!debouncedSearch && (
                <button onClick={() => { setEditingNote(null); setNoteModalOpen(true); }} className="mt-3 text-sm text-primary hover:underline flex items-center gap-1">
                  <Plus size={14} /> {t('notepad.createFirst')}
                </button>
              )}
            </div>
          ) : (
            <div className={view === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'flex flex-col gap-3'}>
              {notes.map(note => (
                <NoteCard key={note.id} note={note} view={view}
                  onPreview={() => setPreviewNote(note)}
                  onEdit={() => { setEditingNote(note); setNoteModalOpen(true); }}
                  onDelete={() => setDeleteConfirm({ type: 'note', id: note.id, name: note.title })}
                  onImageClick={(imgs, idx) => setLightbox({ images: imgs, idx })} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modallar */}
      <NotePreviewModal isOpen={!!previewNote} onClose={() => setPreviewNote(null)} note={previewNote}
        onEdit={() => { setEditingNote(previewNote); setNoteModalOpen(true); }}
        currentUserId={user?.id ?? null} />

      <CategoryModal isOpen={catModalOpen} onClose={() => { setCatModalOpen(false); setEditingCategory(null); }} onSave={saveCategory} initial={editingCategory} />

      <NoteModal isOpen={noteModalOpen} onClose={() => { setNoteModalOpen(false); setEditingNote(null); }}
        onSave={saveNote} onCreateCategory={createCategory} initial={editingNote} categories={categories} defaultCategoryId={defaultCategoryId} />

      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-sm w-full">
          <div className="p-4 md:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{deleteConfirm?.type === 'category' ? t('notepad.deleteCatTitle') : t('notepad.deleteNoteTitle')}</h3>
              <button onClick={() => setDeleteConfirm(null)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              <strong>"{deleteConfirm?.name}"</strong>
              {' '}{deleteConfirm?.type === 'category' ? t('notepad.deleteCatMsg') : t('notepad.deleteNoteMsg')}
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">{t('notepad.cancel')}</button>
              <button onClick={async () => {
                if (!deleteConfirm) return;
                try { if (deleteConfirm.type === 'category') await deleteCategory(deleteConfirm.id); else await deleteNote(deleteConfirm.id); }
                catch {} setDeleteConfirm(null);
              }} className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center justify-center gap-1">
                <Trash2 size={14} /> {t('notepad.delete')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default NotepadPage;
